const SIGN_OUT_PATH = '/authentication/sign-out';
const SIGN_IN_PATH = '/authentication/sign-in/';
const OAUTH_STORAGE_PREFIX = 'alexandria:oauth';

// Server-side calls below are best-effort: none of them may block the user on
// the spinner indefinitely, so every fetch is capped at this timeout.
const LOGOUT_REQUEST_TIMEOUT_MS = 4000;

function getNormalizedPathname() {
  return window.location.pathname.replace(/\/+$/, '') || '/';
}

function isSignOutPage() {
  return getNormalizedPathname() === SIGN_OUT_PATH;
}

function clearClientSession() {
  localStorage.removeItem('jwt_token');
  window.TenantContext?.clearTenantContext();
  sessionStorage.removeItem('userRole');
  sessionStorage.removeItem('mfa:ok');
  sessionStorage.removeItem('canUseAdminMode');
  // ALEX-1321: clear feature-flag session cache
  try {
    var _pfLib = sessionStorage.getItem('pf_features_lib');
    if (_pfLib) sessionStorage.removeItem('pf_features_v1_' + _pfLib);
    sessionStorage.removeItem('pf_features_lib');
  } catch (_) {}
  clearOauthStorage();
  document.documentElement.removeAttribute('data-ui-role');
}

function getOauthStoragePrefix() {
  return window.ALEXANDRIA_COGNITO?.storagePrefix || OAUTH_STORAGE_PREFIX;
}

function getApiBaseUrl() {
  const configured = window.__ALEXANDRIA_CONFIG__?.apiBaseUrl;
  const fromWindow = typeof window.API_BASE_URL === 'string' ? window.API_BASE_URL : '';
  const fromGlobal = typeof API_BASE_URL === 'string' ? API_BASE_URL : '';
  return String(configured || fromWindow || fromGlobal || '').trim().replace(/\/+$/g, '');
}

function clearOauthStorage() {
  // Cleans up any leftover PKCE transaction state from the legacy Hosted-UI
  // redirect path (auth now runs through in-app username/password against
  // Cognito, not the OAuth authorize/callback flow), in case a session was
  // ever half-started there.
  const prefix = getOauthStoragePrefix();
  [
    'pool',
    'last_pool',
    'state',
    'code_verifier',
    'redirect_uri',
    'created_at',
  ].forEach((key) => sessionStorage.removeItem(`${prefix}:${key}`));
  sessionStorage.removeItem('_pendingTenantSelection');
}

// Races a fetch against a timeout so a hung request can't leave the user on
// an infinite spinner. Resolves to `null` on timeout or network error instead
// of rejecting, since every caller treats this as best-effort.
function fetchWithTimeout(url, options, timeoutMs) {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(null);
    }, timeoutMs);

    fetch(url, options)
      .then((response) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(response);
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(null);
      });
  });
}

// Revokes the current Cognito session server-side (access/refresh tokens are
// actually invalidated, not just the local cookies). Requires an authenticated
// request, so this can 401 if the session already expired — that's fine, the
// unconditional clearServerSession() call below still clears cookies either way.
async function revokeCognitoSession() {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) return;

  const response = await fetchWithTimeout(
    `${apiBaseUrl}/api/auth/global-signout/`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    },
    LOGOUT_REQUEST_TIMEOUT_MS,
  );

  if (response === null) {
    console.warn('[logout] Global sign-out request timed out or failed to reach the server');
    return;
  }
  if (!response.ok && response.status !== 401) {
    console.warn('[logout] Global sign-out returned non-OK status', { status: response.status });
  }
}

// Always-clears-cookies fallback (AllowAny): guarantees the auth cookies are
// gone even if the session was already invalid and revokeCognitoSession() 401s.
async function clearServerSession() {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) return;

  const response = await fetchWithTimeout(
    `${apiBaseUrl}/api/auth/cognito/logout/`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    },
    LOGOUT_REQUEST_TIMEOUT_MS,
  );

  if (response === null) {
    console.warn('[logout] Logout bridge request timed out or failed to reach the server');
    return;
  }
  if (!response.ok) {
    console.warn('[logout] Logout bridge returned non-OK status', { status: response.status });
  }
}

function updateSignOutStatus(message) {
  document.querySelectorAll('[data-signout-status]').forEach((node) => {
    node.textContent = message;
  });
}

let signOutPromise = null;

async function executeSignOut() {
  if (signOutPromise) {
    return signOutPromise;
  }

  signOutPromise = (async () => {
    updateSignOutStatus('Cerrando sesión...');

    // Both requests are independent, best-effort, and individually
    // timeout-guarded, so neither can block the redirect below.
    await Promise.all([revokeCognitoSession(), clearServerSession()]);

    clearClientSession();
    window.location.replace(SIGN_IN_PATH);
  })();

  return signOutPromise;
}

function handleSignOutClick(event) {
  event.preventDefault();
  void executeSignOut();
}

function attachSignOutLinks() {
  document.querySelectorAll('[data-signout-link]').forEach((link) => {
    link.addEventListener('click', handleSignOutClick);
  });
}

attachSignOutLinks();

if (isSignOutPage()) {
  void executeSignOut();
}
